import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, ScanLine, Camera, ImageUp, KeyRound, AlertTriangle, CheckCircle2, RotateCcw, ListPlus,
} from "lucide-react";
import {
  normaliseBarcode, lookupProductByBarcodeLocal, mapProductNutritionToIngredient,
  calculateIngredientMacrosFromServing, validateProductNutrition, decodeBarcodeFromImage,
} from "../utils/barcodeNutritionUtils";
import { MOCK_DATA_NOTICE } from "../data/productNutritionMockData";

/* ════════════════════════════════════════════════════════════════════════
   INGREDIENT SCANNER MODAL
   Scan a barcode/QR from ingredient packaging (or upload a photo, or enter
   the code manually), preview the matched product, choose quantity/unit,
   then add a structured ingredient line to the parent recipe.
   ════════════════════════════════════════════════════════════════════════ */

const UNIT_OPTIONS = ["g", "ml", "serving", "tbsp", "tsp", "cup", "slice", "egg", "packet", "other"];

export function IngredientScannerModal({ open, onClose, onAddIngredient }) {
  const [mode, setMode] = useState("manual"); // "camera" | "upload" | "manual"
  const [code, setCode] = useState("");
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState("serving");
  const [note, setNote] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const videoRef = useRef(null);
  const fileRef  = useRef(null);
  const streamRef = useRef(null);

  /* Reset state on each open. */
  useEffect(() => {
    if (!open) return;
    setMode("manual"); setCode(""); setProduct(null); setNotFound(false);
    setQuantity(1); setUnit("serving"); setNote(""); setCameraError("");
  }, [open]);

  /* Close on Escape. */
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Live camera preview (no auto-decoding — capture-and-decode happens on
     "Capture frame" since we don't have an in-browser decoder yet). */
  useEffect(() => {
    if (!open || mode !== "camera") {
      stopCamera();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraError("");
      } catch (e) {
        setCameraError("Camera scanning is not available on this device. You can upload a photo or enter the code manually.");
      }
    })();
    return () => { cancelled = true; stopCamera(); };
  }, [open, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopCamera = () => {
    const s = streamRef.current;
    if (s) { s.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const handleClose = () => { stopCamera(); onClose?.(); };

  const lookup = (raw) => {
    const clean = normaliseBarcode(raw);
    if (!clean) { toast("Enter a barcode first."); return; }
    setCode(clean);
    const p = lookupProductByBarcodeLocal(clean);
    if (p) {
      const { ok, warnings } = validateProductNutrition(p);
      if (!ok) toast(`Heads up: ${warnings.join(", ")}`);
      setProduct(p);
      setUnit(p.servingUnit || "serving");
      setQuantity(1);
      setNotFound(false);
    } else {
      setProduct(null);
      setNotFound(true);
    }
  };

  const tryDecodeFromImage = async (fileOrBlob) => {
    setDecoding(true);
    try {
      const decoded = await decodeBarcodeFromImage(fileOrBlob);
      if (decoded) lookup(decoded);
      else toast("Couldn't auto-decode that image — please type the barcode manually.");
    } catch {
      toast("Couldn't auto-decode that image — please type the barcode manually.");
    } finally {
      setDecoding(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await tryDecodeFromImage(file);
  };

  const captureFrame = async () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width  = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      if (!blob) { toast("Couldn't capture frame."); return; }
      await tryDecodeFromImage(blob);
    }, "image/jpeg", 0.85);
  };

  const macros = product ? calculateIngredientMacrosFromServing(product, { quantity, unit }) : null;

  const addToRecipe = () => {
    if (!product) return;
    const ingredient = mapProductNutritionToIngredient(product, { quantity, unit, note });
    onAddIngredient?.(ingredient);
    toast.success(`${product.productName} added to ingredients.`);
    handleClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="ingredient-scanner-modal"
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-lg rounded-3xl bg-white dark:bg-slate-900 shadow-none border border-[color:var(--ara-line-soft)] dark:border-rose-900/40 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3     dark: dark: dark: border-b border-[color:var(--ara-line-soft)] dark:border-rose-900/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl    flex items-center justify-center flex-shrink-0">
              <ScanLine className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50 leading-tight">Scan ingredient packaging</h2>
              <p className="text-xs font-bold text-gray-500 mt-0.5">Scan a barcode or QR code, then review the ingredient before adding it to your recipe.</p>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-xl hover:bg-white/60 dark:hover:bg-slate-800 flex items-center justify-center text-gray-400"
              aria-label="Close"
              data-testid="ingredient-scanner-close"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-3">
          <div className="rounded-xl bg-transparent dark:bg-transparent border border-[color:var(--ara-line-soft)] dark:border-amber-800 px-3 py-2 flex items-start gap-2" data-testid="ingredient-scanner-notice">
            <AlertTriangle className="w-3.5 h-3.5 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] leading-snug">
              <span className="uppercase tracking-wider font-extrabold">{MOCK_DATA_NOTICE}</span> Real barcode lookup will arrive when a secure product database is connected. Nutrition values are estimates — review before saving.
            </p>
          </div>

          {/* Mode toggle */}
          <div className="grid grid-cols-3 gap-1.5">
            <ModeBtn icon={Camera}   label="Use camera"    active={mode === "camera"}  onClick={() => setMode("camera")}  testId="scanner-mode-camera" />
            <ModeBtn icon={ImageUp}  label="Upload photo"  active={mode === "upload"}  onClick={() => { setMode("upload"); fileRef.current?.click(); }} testId="scanner-mode-upload" />
            <ModeBtn icon={KeyRound} label="Enter manually" active={mode === "manual"} onClick={() => setMode("manual")} testId="scanner-mode-manual" />
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="scanner-file-input" />

          {/* Camera */}
          {mode === "camera" && (
            <div className="space-y-2" data-testid="scanner-camera-panel">
              {cameraError ? (
                <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">{cameraError}</p>
              ) : (
                <>
                  <div className="relative rounded-2xl overflow-hidden border-2 border-[color:var(--ara-line-soft)] dark:border-rose-800 bg-black aspect-video">
                    <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
                    <div className="absolute inset-x-4 inset-y-6 border-2 border-dashed border-white/70 rounded-xl pointer-events-none" aria-hidden />
                  </div>
                  <button
                    type="button"
                    onClick={captureFrame}
                    disabled={decoding}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-transparent hover:opacity-80 text-white text-sm font-extrabold disabled:opacity-60"
                    data-testid="scanner-capture-btn"
                  >
                    {decoding ? "Decoding…" : (<><ScanLine className="w-4 h-4" strokeWidth={1.5} /> Capture &amp; decode</>)}
                  </button>
                  <p className="text-[10px] italic text-gray-500 leading-snug">
                    In-browser decoding isn't available yet — capturing the frame falls back to manual entry. You can also type the code below.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Manual entry — always visible for fallback */}
          <div className="space-y-1.5">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">Barcode</p>
            <div className="flex gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. 9415137001157"
                inputMode="numeric"
                className="flex-1 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
                data-testid="scanner-code-input"
              />
              <button
                type="button"
                onClick={() => lookup(code)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-transparent hover:opacity-80 text-white text-xs font-extrabold"
                data-testid="scanner-lookup-btn"
              >
                Look up
              </button>
            </div>
          </div>

          {/* Product preview */}
          {product && (
            <div className="rounded-2xl border border-[color:var(--ara-line-soft)] dark:border-emerald-800 bg-transparent dark:bg-transparent p-3 space-y-2" data-testid="scanner-product-preview">
              <div className="flex items-center gap-2.5">
                <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-white dark:bg-slate-800 shadow-sm flex-shrink-0">
                  {product.imageUrl
                    ? <img src={product.imageUrl} alt={product.productName} className="w-full h-full object-cover" />
                    : <span className="text-xl" aria-hidden>🥫</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 truncate" data-testid="scanner-product-name">{product.productName}</p>
                  <p className="text-[11px] font-bold text-gray-600 dark:text-gray-300 truncate">{product.brand} · {product.servingSize}{product.servingUnit} serve · {product.nutritionPerServe.calories} kcal</p>
                  {product.allergens?.length > 0 && (
                    <p className="text-[10px] italic text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mt-0.5">Allergens: {product.allergens.join(", ")}</p>
                  )}
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-transparent dark:bg-[color:var(--ara-ink)] text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> Mock match
                </span>
              </div>

              {/* Quantity / unit */}
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Quantity used</span>
                  <input
                    type="number" min="0" step="0.1"
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value) || 0)}
                    className="mt-1 w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100"
                    data-testid="scanner-quantity"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">Unit</span>
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100"
                    data-testid="scanner-unit"
                  >
                    {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>
              </div>

              {/* Computed macros for chosen quantity */}
              <div className="grid grid-cols-4 gap-1.5 text-[10px] font-bold">
                <Macro label="Est. kcal" value={macros?.calories} testId="scanner-macros-cal" />
                <Macro label="Est. P g"  value={macros?.protein}  testId="scanner-macros-p" />
                <Macro label="Est. C g"  value={macros?.carbs}    testId="scanner-macros-c" />
                <Macro label="Est. F g"  value={macros?.fat}      testId="scanner-macros-f" />
              </div>

              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note (e.g. drained, melted)…"
                className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-100"
                data-testid="scanner-note"
              />
            </div>
          )}

          {notFound && !product && (
            <div className="rounded-2xl border border-[color:var(--ara-line-soft)] dark:border-amber-800 bg-transparent dark:bg-transparent p-3 space-y-1.5" data-testid="scanner-not-found">
              <p className="text-xs font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> Product not found in local demo data.
              </p>
              <ul className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] list-disc ml-4 space-y-0.5">
                <li>Enter the product manually as an ingredient line below.</li>
                <li>Or upload a packaging photo (manual decode for now).</li>
                <li>Future product database lookup coming later — no live calls yet.</li>
              </ul>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 dark:border-slate-800 flex flex-col sm:flex-row gap-2 sm:justify-between">
          <button
            type="button"
            onClick={() => { setCode(""); setProduct(null); setNotFound(false); }}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200"
            data-testid="scanner-reset"
          >
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} /> Clear
          </button>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center justify-center px-4 py-2 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm font-extrabold text-gray-700 dark:text-gray-200"
              data-testid="scanner-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addToRecipe}
              disabled={!product}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-2xl bg-transparent hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-extrabold shadow-sm active:scale-[0.98]"
              data-testid="scanner-add-btn"
            >
              <ListPlus className="w-4 h-4" strokeWidth={1.5} /> Add to recipe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModeBtn({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[11px] font-extrabold transition ${active
        ? "bg-transparent text-white border-rose-500"
        : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 hover:border-[color:var(--ara-line-soft)]"}`}
      data-testid={testId}
      aria-pressed={active}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      {label}
    </button>
  );
}

function Macro({ label, value, testId }) {
  const display = value == null || Number.isNaN(value) ? "—" : `~${value}`;
  return (
    <div className="rounded-lg border border-[color:var(--ara-line-soft)] dark:border-emerald-800 bg-white dark:bg-slate-900 px-1.5 py-1">
      <p className="text-[9px] font-extrabold uppercase tracking-wider text-gray-500 leading-none">{label}</p>
      <p className="text-xs font-extrabold text-gray-900 dark:text-gray-50 mt-0.5" data-testid={testId}>{display}</p>
    </div>
  );
}

export default IngredientScannerModal;
